import { Box, Divider, MenuItem, MenuList, Typography } from '@mui/material';
import React from 'react';

import { DATA_TEST } from '../enums';

import { IllustratedMessage } from '../Components/IllustratedMessage';
import { LoadingBlock } from '../Components/LoadingBlock';
import { SubmenuItem } from '../Components/SubmenuItem';
import { DEFAULT_LOADING_ROWS_FOR_PARENT_PANEL } from '../Components/utils';
import { useMenuContext } from '../ContextState/useMenuContext';
import { IMenuItem, ISubmenuSection } from '../types';
import { handleTabOrEscapeToLeaveSubmenu } from '../utils/keyboardNavUtils';
import { itemIsLeafNode, useEnhancedEffect } from '../utils/utils';

interface ILevelTwoContainerProps {
  submenuSections: ISubmenuSection[];
  headerErrorMessage?: string;
  showLoadingIndicator?: boolean;
  showErrorMessage?: boolean;
}

export const LevelTwoContainer: React.FC<ILevelTwoContainerProps> = ({
  submenuSections,
  headerErrorMessage,
  showLoadingIndicator,
  showErrorMessage
}) => {
  const {
    favorites,
    disableFavorites,
    onFavoriteIconClick,
    setSelectedLevelTwoId,
    selectedLevelTwoId,
    setCurrentLevelOneItem,
    currentLevelOneItem,
    customErrorMessage,
    currentLevelOneItemRef,
    currentLevelTwoItemRef,
    currentFirstLevelThreeItemRef,
    currentFirstLevelTwoItemRef
  } = useMenuContext();

  const menuItemRefs = submenuSections
    ?.flatMap((section) => section.items)
    .reduce((acc, item) => {
      if (!item) return acc;
      acc[item.id] = React.createRef<HTMLLIElement>();
      return acc;
    }, {});

  const [hasInteractedWithLevelTwo, setHasInteractedWithLevelTwo] = React.useState(false);

  useEnhancedEffect(() => {
    if (hasInteractedWithLevelTwo) {
      // This effect was being triggered slightly before the L3 menu items were assigned their ref objects.
      // By waiting 1ms, this job gets pushed to the bottom of the Javascript call stack and so by the time it
      // runs, ref.current has a value and can be focused
      setTimeout(() => {
        if (currentFirstLevelThreeItemRef?.current) {
          currentFirstLevelThreeItemRef?.current?.focus();
          setHasInteractedWithLevelTwo(false);
        }
      }, 1);
    }
  }, [currentFirstLevelThreeItemRef.current, hasInteractedWithLevelTwo]);

  React.useEffect(() => {
    if (selectedLevelTwoId) {
      currentLevelTwoItemRef.current = menuItemRefs?.[selectedLevelTwoId]?.current;
    }
  }, [selectedLevelTwoId]);

  React.useEffect(() => {
    const firstItem = submenuSections?.flatMap((section) => section.items)[0];
    if (firstItem?.id && menuItemRefs?.[firstItem.id]) {
      currentFirstLevelTwoItemRef.current = menuItemRefs[firstItem.id].current;
    }
  }, [menuItemRefs, submenuSections]);

  const handleMenuItemClick = (menuItem: IMenuItem) => {
    if (menuItem.onClick) {
      menuItem.onClick();
    }
    if (menuItem.url || menuItem.onClick) {
      // Important for url as well to ensure dropdown closes under all circumstances
      setCurrentLevelOneItem(null);
      return;
    }
    setSelectedLevelTwoId(menuItem.id);
    setHasInteractedWithLevelTwo(true);
  };

  const handleKeyDown = (isLeafNode: boolean, isSelected: boolean, event: React.KeyboardEvent) => {
    handleTabOrEscapeToLeaveSubmenu(event.key, currentLevelOneItemRef?.current);
    if (event.key === 'ArrowRight' && !isLeafNode) {
      // If the L3 submenu for this is already open, shift focus to first item
      if (isSelected && currentFirstLevelThreeItemRef.current) {
        currentFirstLevelThreeItemRef.current.focus();
      } else {
        (event.target as HTMLButtonElement).click();
      }
    }
  };

  if (showErrorMessage) {
    return (
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          justifyContent: 'center',
          padding: 3,
          width: '100%',
          textAlign: 'center'
        }}
      >
        <IllustratedMessage
          illustrationType="error-loading"
          dataTest={DATA_TEST.submenu_level_two_error_state}
          headerText={headerErrorMessage}
          messageText={customErrorMessage || undefined}
          illustrationHeight={139}
          illustrationWidth={239}
        />
      </Box>
    );
  }

  if (showLoadingIndicator) {
    return (
      <Box sx={{ width: '300px', height: '100%', padding: 1 }} data-test={DATA_TEST.level_two_loading_menu}>
        <LoadingBlock numberOfRows={DEFAULT_LOADING_ROWS_FOR_PARENT_PANEL} />
      </Box>
    );
  }

  if (submenuSections.length === 0) return null;

  const getIconType = (leafNode: boolean, favoritesDisabled: boolean) => {
    if (leafNode && !favoritesDisabled) {
      return 'favorite';
    }
    if (leafNode) {
      return undefined;
    }
    return 'rightArrow';
  };

  return (
    <MenuList
      data-test={DATA_TEST.submenu_level_two_container}
      sx={{
        mt: 1,
        mb: 1,
        height: 'auto',
        maxWidth: '290px',
        minWidth: '200px',
        width: 'fit-content',
        overflow: 'auto',
        padding: 0,
        border: 'none',
        boxShadow: 'none',
        borderRadius: 0
      }}
    >
      {submenuSections.map((submenuSection, sectionIdx) => {
        const { title, items: submenuItems } = submenuSection;
        const firstSection = sectionIdx === 0;
        const sectionDivider = firstSection ? null : <Divider component="li" />;

        const titleElement = title ? (
          <MenuItem
            disabled={true}
            sx={{
              opacity: '1 !important',
              px: 2,
              py: 0.25,
              mt: firstSection ? 1 : 2,
              mb: 1,
              '&.Mui-disabled': {
                backgroundColor: 'transparent',
                color: 'inherit'
              }
            }}
          >
            <Typography variant="h5" component="span" aria-hidden={true} key={title}>
              {title}
            </Typography>
          </MenuItem>
        ) : null;

        const submenuItemElements = submenuItems?.map((submenuItem, itemIdx) => {
          const isLeafNode = itemIsLeafNode(submenuItem);

          const isSelected = submenuItem.id === selectedLevelTwoId;
          const isFavorited =
            // Find all favorites in context associated with the parent level one item
            // Then check if the current level two item is in that favorites list
            favorites
              ?.find((fav) => fav.id === currentLevelOneItem?.id)
              ?.items.some((fav) => fav.id === submenuItem.id) ?? false;
          const handleFavoriteIconClick = async (isItemFavorited: boolean) => {
            await onFavoriteIconClick(isItemFavorited, submenuItem);
          };
          return (
            <SubmenuItem
              ref={menuItemRefs[submenuItem.id]}
              item={submenuItem}
              key={submenuItem.id}
              autoFocus={sectionIdx === 0 && itemIdx === 0}
              isSelected={isSelected}
              isFavorited={isFavorited}
              handleMenuClick={() => {
                handleMenuItemClick(submenuItem);
              }}
              lightFontWeight={true}
              handleFavoriteIconClick={handleFavoriteIconClick}
              handleKeyDown={(event) => handleKeyDown(isLeafNode, isSelected, event)}
              icon={getIconType(isLeafNode, disableFavorites)}
            />
          );
        });

        // MUI MenuList does not permit React.Fragments as children
        // Returning an array of element is an alternative to circumvent this limitation
        return [sectionDivider, titleElement, submenuItemElements];
      })}
    </MenuList>
  );
};
